"use client";

import { useState } from "react";
import { ArrowRight, Clock3, Radar, Sparkles } from "lucide-react";
import type { Post } from "@/types/post";
import { Link } from "@/i18n/navigation";
import MiroFishSiteFooter from "./site-footer";
import MiroFishSiteHeader from "./site-header";

const formatDate = (value?: string) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const estimateReadTime = (content?: string) => {
  const words = (content || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`]*`/g, " ")
    .replace(/[#>*_[\]-]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

  return Math.max(1, Math.ceil(words / 220));
};

const formatTagLabel = (tag: string) =>
  tag
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const TOPIC_ORDER = [
  "simulation",
  "forecasting",
  "public opinion",
  "trust",
  "scenario planning",
];

const getTopicOptions = (posts: Post[]) => {
  const tagSet = new Set<string>();

  posts.forEach((post) => {
    (post.tags ?? []).forEach((tag) => {
      const normalized = tag.trim().toLowerCase();
      if (normalized && normalized !== "mirofish") {
        tagSet.add(normalized);
      }
    });
  });

  const ordered = TOPIC_ORDER.filter((tag) => tagSet.has(tag));
  const remaining = Array.from(tagSet)
    .filter((tag) => !TOPIC_ORDER.includes(tag))
    .sort((left, right) => left.localeCompare(right));

  return ["all", ...ordered, ...remaining];
};

const getFeaturedPost = (posts: Post[]) =>
  posts.find((post) => post.slug === "what-is-mirofish") ?? posts[0];

const getSupportPosts = (posts: Post[], featuredSlug?: string) =>
  posts.filter((post) => post.slug !== featuredSlug).slice(0, 6);

export default function MiroFishBlogPage({ posts }: { posts: Post[] }) {
  const [selectedTopic, setSelectedTopic] = useState("all");

  const featuredPost = getFeaturedPost(posts);
  const topicOptions = getTopicOptions(posts);
  const filteredPosts =
    selectedTopic === "all"
      ? getSupportPosts(posts, featuredPost?.slug)
      : getSupportPosts(
          posts.filter((post) =>
            (post.tags ?? []).some(
              (tag) => tag.trim().toLowerCase() === selectedTopic
            )
          ),
          featuredPost?.slug
        );
  const allGuidePosts = posts.filter((post) => post.slug !== featuredPost?.slug);

  return (
    <div
      className="min-h-screen bg-[#04050a] text-white"
      data-testid="mirofish-blog-page"
    >
      <MiroFishSiteHeader hashHrefPrefix="/" />

      <main id="main-content">
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute left-[10%] top-0 h-[360px] w-[360px] rounded-full bg-sky-500/18 blur-[120px]" />
            <div className="absolute right-[8%] top-[10%] h-[320px] w-[320px] rounded-full bg-orange-500/14 blur-[120px]" />
            <div className="absolute bottom-[-15%] left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-violet-500/10 blur-[140px]" />
          </div>

          <div className="mx-auto max-w-7xl px-4 pb-16 pt-12 sm:px-6 lg:px-8 lg:pb-24 lg:pt-18">
            <div
              className="relative z-10 overflow-hidden rounded-[36px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))] p-6 shadow-[0_20px_120px_rgba(0,0,0,0.35)] sm:p-8 lg:p-10"
              data-testid="mirofish-blog-hero"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/6 px-4 py-2 text-xs uppercase tracking-[0.24em] text-white/65">
                Field Notes
              </div>

              <div className="mt-8 max-w-4xl">
                <h1 className="text-balance text-5xl font-semibold leading-[0.95] tracking-[-0.04em] sm:text-6xl lg:text-7xl">
                  Blog
                </h1>
                <p className="mt-6 max-w-2xl text-lg leading-8 text-white/72">
                  Notes, experiments, and operating updates from the MiroFish
                  simulation engine.
                </p>
              </div>

              <div
                className="mt-12 rounded-[32px] border border-white/10 bg-black/25 p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] sm:p-8"
                data-testid="mirofish-blog-body"
              >
                {featuredPost ? (
                  <div className="grid gap-5 lg:grid-cols-[minmax(0,1.08fr)_minmax(320px,0.92fr)]">
                    <Link
                      href={`/blog/${featuredPost.slug}` as any}
                      className="group rounded-[28px] border border-white/10 bg-white/5 p-6 transition hover:border-white/20 hover:bg-white/[0.07]"
                      data-testid="mirofish-blog-featured-card"
                    >
                      <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.22em] text-white/45">
                        <span className="inline-flex items-center gap-2 rounded-full border border-sky-400/20 bg-sky-400/10 px-3 py-1 text-sky-200">
                          <Sparkles className="h-3.5 w-3.5" />
                          Featured Guide
                        </span>
                        <span>{formatDate(featuredPost.created_at)}</span>
                        <span className="inline-flex items-center gap-2">
                          <Clock3 className="h-3.5 w-3.5" />
                          {estimateReadTime(featuredPost.content)} min read
                        </span>
                      </div>

                      <h2 className="mt-5 max-w-3xl text-3xl font-semibold leading-tight text-white sm:text-4xl">
                        {featuredPost.title}
                      </h2>
                      <p className="mt-4 max-w-2xl text-base leading-7 text-white/62">
                        {featuredPost.description}
                      </p>

                      <div className="mt-6 flex flex-wrap gap-2">
                        {(featuredPost.tags ?? [])
                          .filter((tag) => tag.toLowerCase() !== "mirofish")
                          .slice(0, 3)
                          .map((tag) => (
                            <span
                              key={tag}
                              className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-[0.18em] text-white/60"
                            >
                              {formatTagLabel(tag)}
                            </span>
                          ))}
                      </div>

                      <div className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-white">
                        Read field note
                        <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                      </div>
                    </Link>

                    <div className="rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(14,165,233,0.12),rgba(249,115,22,0.08))] p-6">
                      <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                        Prompt Starter
                      </div>
                      <h2 className="mt-4 text-2xl font-semibold text-white">
                        Seed one scenario before you read the whole archive
                      </h2>
                      <p className="mt-4 text-sm leading-7 text-white/62">
                        Start with one report, one pressure event, and one
                        question about how public reaction evolves across
                        platforms.
                      </p>

                      <div className="mt-6 rounded-[24px] border border-white/10 bg-black/35 p-4 text-sm leading-7 text-white/72">
                        Forecast how the uploaded report changes public
                        reaction, who amplifies it first, and what narrative
                        breaks trust over the next three rounds.
                      </div>

                      <div className="mt-6 flex flex-wrap gap-3">
                        <Link
                          href="/#console"
                          className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-medium text-black transition hover:bg-white/90"
                        >
                          <Radar className="h-4 w-4" />
                          Start Engine
                        </Link>
                        <a
                          href="https://www.bilibili.com/video/BV1VYBsBHEMY/"
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-medium text-white transition hover:bg-white/10"
                        >
                          Watch Demo
                          <ArrowRight className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="rounded-[24px] border border-white/10 bg-white/5 p-6 text-sm text-white/58"
                    data-testid="mirofish-blog-empty-state"
                  >
                    The first field notes are being prepared.
                  </div>
                )}

                <div className="mt-12">
                  <div className="mb-5 flex flex-wrap items-center gap-3">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/42">
                      Topic Filters
                    </div>
                    <div className="h-px min-w-16 flex-1 bg-white/10" />
                  </div>

                  <div
                    className="flex flex-wrap gap-3"
                    data-testid="mirofish-blog-filters"
                  >
                    {topicOptions.map((topic) => {
                      const active = topic === selectedTopic;

                      return (
                        <button
                          key={topic}
                          type="button"
                          onClick={() => setSelectedTopic(topic)}
                          className={`rounded-full border px-4 py-2 text-xs uppercase tracking-[0.2em] transition ${
                            active
                              ? "border-sky-300/40 bg-sky-300/12 text-sky-100"
                              : "border-white/10 bg-white/5 text-white/55 hover:bg-white/10 hover:text-white/80"
                          }`}
                        >
                          {topic === "all" ? "All" : formatTagLabel(topic)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div
                  className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-3"
                  data-testid="mirofish-blog-grid"
                >
                  {filteredPosts.map((post) => (
                    <Link
                      key={post.slug}
                      href={`/blog/${post.slug}` as any}
                      className="group rounded-[28px] border border-white/10 bg-white/5 p-5 transition hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/[0.07]"
                      data-testid="mirofish-blog-card"
                    >
                      <div className="flex items-center justify-between gap-3 text-xs uppercase tracking-[0.18em] text-white/42">
                        <span>{formatDate(post.created_at)}</span>
                        <span className="inline-flex items-center gap-2">
                          <Clock3 className="h-3.5 w-3.5" />
                          {estimateReadTime(post.content)} min
                        </span>
                      </div>

                      <h3 className="mt-4 text-xl font-semibold leading-snug text-white">
                        {post.title}
                      </h3>
                      <p className="mt-3 text-sm leading-7 text-white/58">
                        {post.description}
                      </p>

                      <div className="mt-5 flex flex-wrap gap-2">
                        {(post.tags ?? [])
                          .filter((tag) => tag.toLowerCase() !== "mirofish")
                          .slice(0, 2)
                          .map((tag) => (
                            <span
                              key={tag}
                              className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[11px] uppercase tracking-[0.16em] text-white/54"
                            >
                              {formatTagLabel(tag)}
                            </span>
                          ))}
                      </div>

                      <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white/88">
                        Open article
                        <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                      </div>
                    </Link>
                  ))}
                </div>

                <nav
                  className="mt-12 border-t border-white/10 pt-10"
                  aria-label="All simulation guides"
                  data-testid="mirofish-blog-directory"
                >
                  <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                      <div className="text-xs uppercase tracking-[0.24em] text-white/42">
                        Complete directory
                      </div>
                      <h2 className="mt-3 text-2xl font-semibold text-white">
                        All simulation guides
                      </h2>
                    </div>
                    <p className="max-w-lg text-sm leading-6 text-white/48">
                      Every published guide remains available in the initial page HTML; filters only change the featured grid above.
                    </p>
                  </div>
                  <ul className="mt-7 grid gap-x-8 gap-y-1 md:grid-cols-2">
                    {allGuidePosts.map((post) => (
                      <li key={post.slug} className="border-t border-white/[0.08]">
                        <Link
                          href={`/blog/${post.slug}` as any}
                          className="group flex items-center justify-between gap-4 py-4 text-sm text-white/64 transition hover:text-white"
                        >
                          <span>{post.title}</span>
                          <ArrowRight className="h-3.5 w-3.5 shrink-0 transition group-hover:translate-x-0.5" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MiroFishSiteFooter />
    </div>
  );
}
